/**
 * Knock **message type** for the workplace rating-prompt guide — the value
 * `useGuides({ type })` filters on. This is the type set on the message in the
 * Knock dashboard, not the guide key (`workplace-app-store-review-soft-prompt`);
 * conflating the two leaves the client filter dropping every guide Knock
 * delivers. Other Knock surfaces (`banner`, `modal`) are untouched.
 */
export const REVIEW_PROMPT_GUIDE_TYPE = "app-store-review-soft-prompt";

/**
 * Destination for the feedback-step "Contact support" button. Opened in the
 * Capacitor in-app browser on native and a new tab on web; the dialog stays
 * mounted behind it so the user can still finish the prompt afterward.
 */
export const REVIEW_PROMPT_SUPPORT_URL =
  "https://support.clipboardhealth.com/hc/en-us/requests/new";

/**
 * Knock user attribute holding the ISO timestamp of the user's most recent
 * terminal interaction with the rating prompt (Rate Us, No, or Ask me later),
 * paired with `lastReviewPromptActionType`. Together they are the single
 * cooldown gate.
 *
 * Why a single timestamp + type is sufficient: the prompt can only be shown
 * again once the prior cooldown has elapsed, so interactions are serialized —
 * only the most recent one can ever be active, and any earlier interaction's
 * cooldown is provably already past. The Knock dashboard re-prompt rules
 * compare this against `now - N days`, one branch per action type, so each
 * window (N) is a plain integer tunable in the dashboard with no deploy.
 *
 * Written on every terminal path (`useRequestInAppReview` for rate;
 * `useRecordReviewPromptCooldown` for decline and dismiss).
 */
export const KNOCK_LAST_REVIEW_PROMPT_SHOWN_AT_ATTRIBUTE = "lastReviewPromptShownAt";

/**
 * Companion discriminator to `lastReviewPromptShownAt`: which terminal action
 * produced that timestamp. Two readers:
 * - Dashboard targeting selects the per-action re-prompt window (one branch
 *   per type: rate, decline, dismiss).
 * - Client mount routing treats "rate" as the returning-rater signal — a user
 *   whose last action was rating skips the modal and fires the native sheet
 *   directly (a rated user never re-enters the modal, so it stays "rate").
 */
export const KNOCK_REVIEW_PROMPT_ACTION_TYPE_ATTRIBUTE = "lastReviewPromptActionType";

/**
 * Allowed values for `lastReviewPromptActionType`. Const map + union per the
 * repo convention (no TypeScript enums).
 */
export const REVIEW_PROMPT_ACTION_TYPES = {
  rate: "rate",
  decline: "decline",
  dismiss: "dismiss",
} as const;

export type ReviewPromptActionType =
  (typeof REVIEW_PROMPT_ACTION_TYPES)[keyof typeof REVIEW_PROMPT_ACTION_TYPES];

/**
 * Knock user attribute holding the ISO timestamp of the user's first session
 * on a Capacitor-native build of the app (iOS or Android). Read by the Knock
 * dashboard's `daysSince(firstMobileAppUseAt)` targeting rule to enforce a
 * "soak time on the mobile app" floor before any rating prompt fires —
 * stricter than Cognito tenure, which Knock can derive from its built-in
 * `recipient.createdAt`.
 *
 * Written exactly once per user-account: `useEmitReviewPromptAttributes`
 * reads the current value via `user.get()` and only writes the timestamp
 * when it is absent. This avoids a cross-device replay where a later mobile
 * install would otherwise overwrite the earliest one (Knock identify is
 * last-write-wins; there is no min-merge primitive). The Knock value
 * becomes "earliest mobile install we observed across any device."
 */
export const KNOCK_FIRST_MOBILE_APP_USE_AT_ATTRIBUTE = "firstMobileAppUseAt";
